import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import LeaveCard from "../../components/leaves/LeaveCard";
import LeaveBalancesPanel from "../../components/leaves/LeaveBalancesPanel";
import OpeningBalancesPanel from "../../components/leaves/OpeningBalancesPanel";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Sparkles,
  Settings2,
  AlertTriangle,
  Loader2,
  Inbox,
  X,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { LEAVE_TYPES, STATUS_OPTIONS, LEAVE_TYPE_LABEL } from "../../lib/leaveOptions";
import { joinParts, plural } from "../../lib/vocabulary";
import StickyHeading from "../../components/common/StickyHeading";

const ALL = "__all__";
const ADJUSTABLE_ROLES = ["sales_officer", "assistant_manager", "admin_manager"];
const ROLE_TAG = { sales_officer: "SO", assistant_manager: "Manager", admin_manager: "Manager" };

export default function AllLeavesPage() {
  const [leaves, setLeaves] = useState([]);
  const [balances, setBalances] = useState([]);
  // Keyed lookup so each pending row can show its filer's remaining days
  // without scanning the list per card.
  const balanceByUser = useMemo(
    () => Object.fromEntries(balances.map((b) => [b.user_id, b])),
    [balances]
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sos, setSos] = useState([]);
  const [adjustUsers, setAdjustUsers] = useState([]); // L4 fix: SOs + managers
  const [soId, setSoId] = useState(ALL);
  const [type, setType] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [lopOnly, setLopOnly] = useState(false);

  // Balance adjust dialog state
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ user_id: "", leave_type: "CL", delta: 0, reason: "" });
  const [adjustSubmitting, setAdjustSubmitting] = useState(false);

  // Approve/reject dialog state (L2 fix)
  const [actionMode, setActionMode] = useState(null);
  const [remark, setRemark] = useState("");
  const [acting, setActing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        setSos(data.filter((u) => u.role === "sales_officer" && u.is_active));
        setAdjustUsers(
          data
            .filter((u) => ADJUSTABLE_ROLES.includes(u.role) && u.is_active)
            .sort((a, b) => a.full_name.localeCompare(b.full_name))
        );
      } catch (_) {
        /* non-fatal */
      }
    })();
  }, []);

  async function load() {
    const params = {};
    if (soId !== ALL) params.so_id = soId;
    if (type !== ALL) params.leave_type = type;
    if (status !== ALL) params.status = status;
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    if (lopOnly) params.is_lop_only = true;

    setLoading(true);
    try {
      const [{ data }, { data: bal }] = await Promise.all([
        api.get("/leaves", { params }),
        api.get("/leaves/balances"),
      ]);
      setLeaves(data);
      setBalances(bal);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soId, type, status, from, to, lopOnly]);

  async function submitAction() {
    if (!actionMode) return;
    if (actionMode.kind === "reject" && !remark.trim()) {
      toast.error("Please add a reason for rejection.");
      return;
    }
    setActing(true);
    try {
      await api.put(`/leaves/${actionMode.leave.id}/${actionMode.kind}`, {
        remark: remark.trim() || null,
      });
      toast.success(actionMode.kind === "approve" ? "Leave approved" : "Leave rejected");
      setActionMode(null);
      setRemark("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setActing(false);
    }
  }

  const hasFilters =
    soId !== ALL || type !== ALL || status !== ALL || !!from || !!to || lopOnly;
  const clear = () => {
    setSoId(ALL);
    setType(ALL);
    setStatus(ALL);
    setFrom("");
    setTo("");
    setLopOnly(false);
  };

  async function submitAdjust(e) {
    e.preventDefault();
    if (!adjustForm.user_id) {
      toast.error("Pick a user");
      return;
    }
    if (!adjustForm.reason.trim()) {
      toast.error("Add a reason");
      return;
    }
    setAdjustSubmitting(true);
    try {
      await api.put("/leaves/balance/adjust", {
        user_id: adjustForm.user_id,
        leave_type: adjustForm.leave_type,
        delta: Number(adjustForm.delta),
        reason: adjustForm.reason.trim(),
      });
      toast.success("Balance updated");
      setAdjustOpen(false);
      setAdjustForm({ user_id: "", leave_type: "CL", delta: 0, reason: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAdjustSubmitting(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="all-leaves-page">
      <StickyHeading
        icon={Sparkles}
        label="Company Leave Admin"
        title="All leaves"
        subtitle="Filter, audit, and adjust leave balances across the company."
        testId="all-leaves-heading"
        actions={
          <Button
            onClick={() => setAdjustOpen(true)}
            className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold"
            data-testid="all-leaves-adjust-button"
          >
            <Settings2 className="h-4 w-4 mr-1.5" />
            Adjust balance
          </Button>
        }
      />

      {/* Filters */}
      <div className="bg-white border border-callisto rounded-xl p-3 md:p-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <Select value={soId} onValueChange={setSoId}>
            <SelectTrigger className="h-11 col-span-2 md:col-span-2 focus:ring-callisto-gold" data-testid="all-leaves-filter-so">
              <SelectValue placeholder="Sales officer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All officers</SelectItem>
              {sos.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="all-leaves-filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All types</SelectItem>
              {LEAVE_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="all-leaves-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            max={to || undefined}
            className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="all-leaves-filter-from"
          />
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            min={from || undefined}
            className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="all-leaves-filter-to"
          />
        </div>
        <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-callisto-body cursor-pointer">
            <input
              type="checkbox"
              checked={lopOnly}
              onChange={(e) => setLopOnly(e.target.checked)}
              data-testid="all-leaves-filter-lop"
              className="h-4 w-4 accent-callisto-gold"
            />
            <span className="inline-flex items-center gap-1">
              <AlertTriangle size={12} className="text-orange-600" />
              Show LOP leaves only
            </span>
          </label>
          {hasFilters && (
            <Button
              variant="ghost"
              onClick={clear}
              className="h-9 text-callisto-muted hover:text-callisto-navy"
              data-testid="all-leaves-filters-clear"
            >
              <X className="h-3 w-3 mr-1" /> Clear filters
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {/* The Director's pre-go-live task (D-004): a PL opening balance per
          person, entered and amendable here rather than by a one-off script.
          Collapsed by default and gated by the same roles as the endpoint —
          anyone else gets a 403 and the panel says so. */}
      <OpeningBalancesPanel />

      <LeaveBalancesPanel balances={balances} loading={loading} />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      ) : leaves.length === 0 ? (
        <div className="bg-white border border-callisto rounded-xl p-10 flex flex-col items-center text-center">
          <div className="h-14 w-14 rounded-xl bg-callisto-navy/5 flex items-center justify-center mb-3">
            <Inbox className="h-6 w-6 text-callisto-navy" />
          </div>
          <p className="text-sm font-semibold text-callisto-navy">No leaves match</p>
          <p className="mt-1 text-xs text-callisto-muted">Try clearing filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="all-leaves-grid">
          {leaves.map((l) => (
            <LeaveCard
              key={l.id}
              leave={l}
              showOfficer
              balance={balanceByUser[l.sales_officer_id]}
              action={
                l.can_approve && l.status === "pending" ? (
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      onClick={() => setActionMode({ leave: l, kind: "approve" })}
                      className="h-7 px-2.5 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`all-leaves-approve-${l.id}`}
                    >
                      <CheckCircle2 size={12} className="mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setActionMode({ leave: l, kind: "reject" })}
                      className="h-7 px-2.5 text-[11px] border-red-200 text-red-700 hover:bg-red-50"
                      data-testid={`all-leaves-reject-${l.id}`}
                    >
                      <XCircle size={12} className="mr-1" />
                      Reject
                    </Button>
                  </div>
                ) : null
              }
            />
          ))}
        </div>
      )}

      {/* Approve/Reject dialog (L2 fix) */}
      <Dialog
        open={!!actionMode}
        onOpenChange={(o) => {
          if (!o) {
            setActionMode(null);
            setRemark("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionMode?.kind === "approve" ? "Approve leave" : "Reject leave"}
            </DialogTitle>
            <DialogDescription>
              {/* Same line, same helpers, as the Team queue's dialog: the
                  separator sits between the parts that exist, and one day is
                  "1 day". */}
              {joinParts([
                actionMode?.leave?.sales_officer_name,
                actionMode?.leave &&
                  `${LEAVE_TYPE_LABEL[actionMode.leave.leave_type]} Leave`,
                actionMode?.leave && plural(actionMode.leave.days_count, "day"),
              ])}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            rows={3}
            placeholder={
              actionMode?.kind === "approve"
                ? "Optional remark"
                : "Reason for rejection (required)"
            }
            className="focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="all-leaves-remark"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionMode(null);
                setRemark("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={submitAction}
              disabled={acting}
              className={
                actionMode?.kind === "approve"
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-red-600 hover:bg-red-700"
              }
              data-testid="all-leaves-action-confirm"
            >
              {acting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : actionMode?.kind === "approve" ? (
                "Approve"
              ) : (
                "Reject"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust balance dialog (L4 fix: includes managers) */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust leave balance</DialogTitle>
            <DialogDescription>
              Push a positive number to grant more days, a negative number to deduct.
              Every adjustment is logged.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAdjust} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-callisto-navy">User</Label>
                <Select
                  value={adjustForm.user_id}
                  onValueChange={(v) => setAdjustForm((p) => ({ ...p, user_id: v }))}
                >
                  <SelectTrigger className="h-11 mt-1.5 focus:ring-callisto-gold" data-testid="adjust-user">
                    <SelectValue placeholder="Pick a user" />
                  </SelectTrigger>
                  <SelectContent>
                    {adjustUsers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {joinParts([s.full_name, ROLE_TAG[s.role]])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-callisto-navy">Leave type</Label>
                <Select
                  value={adjustForm.leave_type}
                  onValueChange={(v) => setAdjustForm((p) => ({ ...p, leave_type: v }))}
                >
                  <SelectTrigger className="h-11 mt-1.5 focus:ring-callisto-gold" data-testid="adjust-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAVE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {LEAVE_TYPE_LABEL[t.value]} ({t.value})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-callisto-navy">Delta (days)</Label>
              <Input
                type="number"
                step="0.5"
                value={adjustForm.delta}
                onChange={(e) => setAdjustForm((p) => ({ ...p, delta: e.target.value }))}
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="adjust-delta"
              />
              <p className="mt-1 text-[11px] text-callisto-muted">Use negative values to deduct.</p>
            </div>
            <div>
              <Label className="text-callisto-navy">Reason</Label>
              <Textarea
                value={adjustForm.reason}
                onChange={(e) => setAdjustForm((p) => ({ ...p, reason: e.target.value }))}
                rows={3}
                placeholder="e.g. Carry-forward from previous year"
                className="mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="adjust-reason"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAdjustOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={adjustSubmitting}
                className="bg-callisto-navy hover:bg-callisto-navy/90"
                data-testid="adjust-submit"
              >
                {adjustSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save adjustment"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
